// App-wide keyboard shortcuts: Ctrl/⌘ K or / opens the command palette, ?
// lists the shortcuts, [ folds the sidebar, and "g" then a letter jumps to a page. They stay out of
// the way while typing and while a dialog is open.
import { useEffect, useEffectEvent } from 'react'

/** `restaurant`: only in restaurant mode. `shop`: never in restaurant mode. */
export const GO_KEYS: {
  key: string
  path: string
  label: string
  restaurant?: boolean
  shop?: boolean
}[] = [
  { key: 'd', path: '/', label: 'Dashboard' },
  { key: 't', path: '/tables', label: 'Tables', restaurant: true },
  { key: 'k', path: '/kitchen', label: 'Kitchen', restaurant: true },
  { key: 'b', path: '/bill', label: 'Quick bill' },
  { key: 'i', path: '/invoices', label: 'Invoices' },
  { key: 'q', path: '/quotations', label: 'Quotations', shop: true },
  // A shop's sales orders, or a restaurant's Orders page: the same address.
  { key: 'o', path: '/orders', label: 'Orders' },
  { key: 'c', path: '/customers', label: 'Customers' },
  { key: 'p', path: '/products', label: 'Products' },
  { key: 's', path: '/stock', label: 'Stock' },
]

/** Shortcut text for a page, e.g. "G B". */
export function goShortcut(path: string): string | undefined {
  const entry = GO_KEYS.find((g) => g.path === path)
  return entry && `G ${entry.key.toUpperCase()}`
}

export function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  return target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)
}

export function dialogOpen(): boolean {
  return document.querySelector('[role="dialog"]') !== null
}

export function useGlobalShortcuts(handlers: {
  palette: () => void
  help: () => void
  sidebar: () => void
  go: (path: string) => void
}) {
  const handle = useEffectEvent((e: KeyboardEvent, pendingG: boolean): boolean => {
    if (e.defaultPrevented || e.altKey || dialogOpen()) return false
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
      e.preventDefault()
      handlers.palette()
      return false
    }
    if (e.ctrlKey || e.metaKey || isTyping(e.target)) return false
    if (pendingG) {
      const entry = GO_KEYS.find((g) => g.key === e.key.toLowerCase())
      if (entry) {
        e.preventDefault()
        handlers.go(entry.path)
      }
      return false
    }
    if (e.key === '/') {
      e.preventDefault()
      handlers.palette()
    } else if (e.key === '?') {
      e.preventDefault()
      handlers.help()
    } else if (e.key === '[') {
      e.preventDefault()
      handlers.sidebar()
    } else if (e.key === 'g') {
      return true
    }
    return false
  })

  useEffect(() => {
    let pendingG = false
    let timer: ReturnType<typeof setTimeout> | undefined
    const onKey = (e: KeyboardEvent) => {
      const next = handle(e, pendingG)
      clearTimeout(timer)
      pendingG = next
      if (next) timer = setTimeout(() => (pendingG = false), 1200)
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
      clearTimeout(timer)
    }
  }, [])
}
